import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, onSnapshot, collection, query, where, addDoc, serverTimestamp, getDoc, Timestamp, runTransaction, getDocs, orderBy, limit } from "firebase/firestore";
import { db } from "../../../Firebase";
import styled, { createGlobalStyle } from "styled-components";
import { useAuth } from "../hooks/useAuth";
import { V3Room, V3Event, V3Problem, V3Submission } from "../types/v3types";

const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
    overflow-x: hidden;
  }
  
  * {
    box-sizing: border-box;
  }
`;

const Container = styled.div`
  min-height: 100vh;
  background: #1a1a1a;
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow-x: hidden;
  background-image: linear-gradient(45deg, #000 25%, #1a1a1a 25%, #1a1a1a 50%, #000 50%, #000 75%, #1a1a1a 75%, #1a1a1a);
  background-size: 40px 40px;
`;

const Header = styled.header`
  background: linear-gradient(180deg, #dc143c 0%, #8b0000 100%);
  color: white;
  padding: 15px 20px;
  box-shadow: 0 4px 15px rgba(220, 20, 60, 0.3);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  border-bottom: 3px solid #ff0000;
  
  @media (max-width: 600px) {
    padding: 10px 15px;
  }
`;

const Title = styled.h1`
  font-size: 20px;
  margin: 0;
  
  @media (max-width: 600px) {
    font-size: 18px;
  }
`;

const StatusInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  
  @media (max-width: 600px) {
    gap: 10px;
  }
`;

const Score = styled.div`
  font-size: 18px;
  font-weight: 500;
  background: rgba(0, 0, 0, 0.3);
  padding: 5px 15px;
  border-radius: 20px;
  
  @media (max-width: 600px) {
    font-size: 16px;
    padding: 5px 10px;
  }
`;

const Content = styled.main`
  flex: 1;
  padding: 20px;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
  
  @media (max-width: 600px) {
    padding: 15px;
  }
`;

const GameCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  padding: 25px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(5px);
  border: 2px solid #dc143c;
  
  @media (max-width: 600px) {
    padding: 20px;
  }
`;

const ProblemNavigation = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 20px 0;
`;

const ProblemRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
`;

const ProblemTab = styled.button<{ isActive: boolean; isLocked: boolean; isAnswered: boolean }>`
  width: 40px;
  height: 40px;
  padding: 0;
  border: 2px solid ${props => props.isActive ? '#dc143c' : '#e0e0e0'};
  background: ${props => {
    if (props.isLocked) return '#e9ecef';
    if (props.isAnswered) return '#d4edda';
    if (props.isActive) return '#dc143c';
    return 'white';
  }};
  color: ${props => {
    if (props.isLocked) return '#6c757d';
    if (props.isActive) return 'white';
    return '#333';
  }};
  border-radius: 8px;
  font-weight: 600;
  font-size: 16px;
  cursor: ${props => props.isLocked ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover:not(:disabled) {
    transform: ${props => props.isLocked ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.isLocked ? 'none' : '0 2px 8px rgba(220, 20, 60, 0.3)'};
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  justify-content: center;
  align-items: center;
`;

const NavButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 8px 16px;
  background: ${props => props.variant === 'primary' 
    ? 'linear-gradient(135deg, #dc143c 0%, #8b0000 100%)' 
    : '#fff'};
  color: ${props => props.variant === 'primary' ? '#fff' : '#dc143c'};
  border: 2px solid #dc143c;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(220, 20, 60, 0.3);
  }
  
  &:disabled {
    background: #e9ecef;
    color: #6c757d;
    border-color: #dee2e6;
    cursor: not-allowed;
    transform: none;
  }
`;

const NavigationDivider = styled.span`
  color: #ccc;
  font-size: 16px;
  margin: 0 8px;
`;

const ProblemContent = styled.div`
  margin-bottom: 30px;
`;

const ProblemImage = styled.img`
  max-width: 100%;
  max-height: 400px;
  object-fit: contain;
  border-radius: 8px;
  margin-bottom: 20px;
  display: block;
  margin-left: auto;
  margin-right: auto;
`;

const AnswerForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const AnswerInput = styled.input`
  padding: 15px 20px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 18px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #dc143c;
    box-shadow: 0 0 0 3px rgba(220, 20, 60, 0.1);
  }

  &:disabled {
    background: #f5f5f5;
  }
`;

const SubmitButton = styled.button`
  padding: 15px 30px;
  background: linear-gradient(135deg, #dc143c 0%, #8b0000 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(220, 20, 60, 0.3);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.div<{ type: 'error' | 'success' | 'info' }>`
  padding: 15px 20px;
  border-radius: 8px;
  margin-top: 20px;
  text-align: center;
  font-weight: 600;
  background: ${props => {
    switch (props.type) {
      case 'error': return '#f8d7da';
      case 'success': return '#d4edda';
      default: return '#d1ecf1';
    }
  }};
  color: ${props => {
    switch (props.type) {
      case 'error': return '#721c24';
      case 'success': return '#155724';
      default: return '#0c5460';
    }
  }};
`;

const LockedMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
  font-size: 18px;
`;

const ProblemInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
`;

const ProblemNumber = styled.h2`
  font-size: 24px;
  color: #333;
  margin: 0;
`;

const ProblemStatus = styled.span<{ status: 'correct' | 'incorrect' | 'unanswered' }>`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  background: ${props => {
    switch (props.status) {
      case 'correct': return '#d4edda';
      case 'incorrect': return '#f8d7da';
      default: return '#e2e3e5';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'correct': return '#155724';
      case 'incorrect': return '#721c24';
      default: return '#383d41';
    }
  }};
`;

const TimerContainer = styled.div`
  width: 200px;
  height: 30px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 15px;
  padding: 3px;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 600px) {
    width: 150px;
    height: 25px;
  }
`;

const TimerBar = styled.div<{ progress: number }>`
  height: 100%;
  background: linear-gradient(90deg, #ff4444 0%, #ff6666 100%);
  border-radius: 13px;
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
  box-shadow: 0 0 10px rgba(255, 68, 68, 0.5);
`;

const TimerText = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 14px;
  font-weight: bold;
  color: white;
  text-shadow: 0 0 3px rgba(0, 0, 0, 0.8);
`;

const V3KachidokiGame: React.FC = () => {
  const { projectId, roomId } = useParams<{ projectId: string; roomId: string }>();
  const navigate = useNavigate();
  const { user: firebaseUser, loading: authLoading } = useAuth();
  const [room, setRoom] = useState<V3Room | null>(null);
  const [event, setEvent] = useState<V3Event | null>(null);
  const [currentProblemIndex, setCurrentProblemIndex] = useState<number>(0);
  const [answer, setAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submissions, setSubmissions] = useState<V3Submission[]>([]);
  const [mySubmissions, setMySubmissions] = useState<V3Submission[]>([]);
  const [totalScore, setTotalScore] = useState(0);
  const [userId, setUserId] = useState<string>("");
  const [lastResult, setLastResult] = useState<{ correct: boolean; points: number; rank?: number } | null>(null);
  const [nextReleaseIn, setNextReleaseIn] = useState<number>(0);

  useEffect(() => {
    if (!projectId || !roomId) {
      navigate("/");
      return;
    }

    if (authLoading) return;

    if (!firebaseUser) {
      navigate(`/v3/${projectId}/start`);
      return;
    }

    const sessionUserId = firebaseUser.uid + "_" + projectId;
    setUserId(sessionUserId);

    // Subscribe to room updates
    const unsubscribeRoom = onSnapshot(doc(db, "v3rooms", roomId), async (docSnapshot) => {
      if (docSnapshot.exists()) {
        const roomData = docSnapshot.data() as V3Room;
        setRoom(roomData);

        // Load event data
        if (roomData.eventId) {
          const eventDocRef = doc(db, "v3events", roomData.eventId);
          const eventDoc = await getDoc(eventDocRef);
          if (eventDoc.exists()) {
            const eventData = eventDoc.data() as V3Event;
            setEvent(eventData);
            
            // Restore released problems based on start time
            if (roomData.kachidokiStatus?.isRunning && roomData.kachidokiStatus?.startTime) {
              const startTime = roomData.kachidokiStatus.startTime.toMillis();
              const now = Date.now();
              const elapsedSeconds = Math.floor((now - startTime) / 1000);
              const releaseInterval = eventData.kachidokiSettings?.releaseInterval || 30;
              
              // Calculate which problems should be released
              const totalProblems = eventData.problems.length;
              const releasedCount = Math.min(
                Math.floor(elapsedSeconds / releaseInterval) + 1, // +1 because first problem is released at start
                totalProblems
              );
              
              // Build the releasedProblems array
              const releasedProblems: number[] = [];
              for (let i = 0; i < releasedCount; i++) {
                releasedProblems.push(i);
              }
              
              // Update room data locally if different from server
              if (JSON.stringify(releasedProblems) !== JSON.stringify(roomData.kachidokiStatus.releasedProblems)) {
                roomData.kachidokiStatus.releasedProblems = releasedProblems;
                
                // Calculate next release time
                if (releasedCount < totalProblems) {
                  const nextReleaseTime = startTime + (releasedCount * releaseInterval * 1000);
                  roomData.kachidokiStatus.nextReleaseTime = Timestamp.fromMillis(nextReleaseTime);
                }
              }
            }
          }
        }
      }
    });

    // Subscribe to all submissions
    const submissionsQuery = query(
      collection(db, "v3submissions"),
      where("roomId", "==", roomId)
    );
    
    const unsubscribeSubmissions = onSnapshot(submissionsQuery, (snapshot) => {
      const allSubs = snapshot.docs.map(doc => doc.data() as V3Submission);
      setSubmissions(allSubs);
      
      const mySubs = allSubs.filter(sub => sub.userId === sessionUserId);
      setMySubmissions(mySubs);
      
      const myScore = mySubs
        .filter(sub => sub.isCorrect)
        .reduce((total, sub) => total + sub.score + (sub.kachidokiPoints || 0), 0);
      setTotalScore(myScore);
    });

    return () => {
      unsubscribeRoom();
      unsubscribeSubmissions();
    };
  }, [projectId, roomId, navigate, firebaseUser, authLoading]);

  useEffect(() => {
    // Calculate countdown to next problem release
    if (!room || !event || !room.kachidokiStatus?.isRunning) {
      setNextReleaseIn(0);
      return;
    }

    const interval = setInterval(() => {
      if (room.kachidokiStatus?.nextReleaseTime) {
        const now = Date.now();
        const releaseTime = room.kachidokiStatus.nextReleaseTime.toMillis();
        const remaining = Math.max(0, Math.ceil((releaseTime - now) / 1000));
        setNextReleaseIn(remaining);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [room, event]);

  // Auto-advance feature disabled
  // useEffect(() => {
  //   // Auto-advance to new problem if conditions are met
  //   if (!room || !event || !event.kachidokiSettings?.autoAdvance) return;

  //   const releasedProblems = room.kachidokiStatus?.releasedProblems || [];
  //   const latestProblemIndex = releasedProblems[releasedProblems.length - 1];
    
  //   // Check if user is viewing the latest problem and has already answered it
  //   if (currentProblemIndex === latestProblemIndex - 1) {
  //     const currentProblem = event.problems[currentProblemIndex];
  //     const hasAnswered = mySubmissions.some(sub => sub.problemId === currentProblem.problemId);
      
  //     if (hasAnswered && releasedProblems.includes(latestProblemIndex)) {
  //       setCurrentProblemIndex(latestProblemIndex);
  //       setAnswer("");
  //       setLastResult(null);
  //     }
  //   }
  // }, [room?.kachidokiStatus?.releasedProblems, currentProblemIndex, mySubmissions, event]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!answer.trim() || submitting || !room || !event || !userId) return;

    const currentProblem = event.problems[currentProblemIndex];
    if (!currentProblem) return;

    // Check if already correctly answered
    const alreadyCorrectlyAnswered = mySubmissions.some(sub => sub.problemId === currentProblem.problemId && sub.isCorrect);
    if (alreadyCorrectlyAnswered) {
      setLastResult({ correct: false, points: 0 });
      return;
    }

    setSubmitting(true);

    try {
      // Normalize answer
      const normalizedAnswer = answer.trim().toLowerCase()
        .replace(/[\u3000\s]+/g, '')
        .replace(/[ぁ-ん]/g, match => String.fromCharCode(match.charCodeAt(0) + 96));

      // Check if correct
      const isCorrect = currentProblem.answers.some(ans => {
        const normalizedCorrect = ans.toLowerCase()
          .replace(/[\u3000\s]+/g, '')
          .replace(/[ぁ-ん]/g, match => String.fromCharCode(match.charCodeAt(0) + 96));
        return normalizedAnswer === normalizedCorrect;
      });

      // Use transaction to calculate rank and submit
      const result = await runTransaction(db, async (transaction) => {
        let score = 0;
        let kachidokiRank = 0;
        let kachidokiPoints = 0;

        if (isCorrect) {
          score = 1; // Base score for correct answer
          
          // Get the submission with the highest rank for this problem
          const maxRankQuery = query(
            collection(db, "v3submissions"),
            where("roomId", "==", roomId),
            where("problemId", "==", currentProblem.problemId),
            where("isCorrect", "==", true),
            orderBy("kachidokiRank", "desc"),
            limit(1)
          );
          
          const querySnapshot = await getDocs(maxRankQuery);
          
          if (querySnapshot.empty) {
            // First correct answer
            kachidokiRank = 1;
          } else {
            // Get the maximum rank and add 1
            const maxRankSubmission = querySnapshot.docs[0].data() as V3Submission;
            kachidokiRank = (maxRankSubmission.kachidokiRank || 0) + 1;
          }
          
          // Calculate ranking points
          const rankingPoints = event.kachidokiSettings?.rankingPoints || [5, 4, 3, 2, 1];
          if (kachidokiRank <= rankingPoints.length) {
            kachidokiPoints = rankingPoints[kachidokiRank - 1];
          }
        }

        // Create new submission document
        const submissionRef = doc(collection(db, "v3submissions"));
        const submissionData = {
          submissionId: `sub_${Date.now()}_${userId}`,
          roomId,
          userId,
          problemId: currentProblem.problemId,
          answer,
          submittedAt: serverTimestamp(),
          isCorrect,
          score,
          submissionNumber: mySubmissions.filter(s => s.problemId === currentProblem.problemId).length + 1,
          timeTaken: 0, // Not used in KACHIDOKI mode
          kachidokiRank,
          kachidokiPoints,
        };
        
        transaction.set(submissionRef, submissionData);
        
        return { isCorrect, score, kachidokiRank, kachidokiPoints };
      });

      setLastResult({ 
        correct: result.isCorrect, 
        points: result.isCorrect ? result.score + result.kachidokiPoints : 0,
        rank: result.isCorrect ? result.kachidokiRank : undefined
      });
      
      if (result.isCorrect) {
        setAnswer("");
      }
    } catch (error) {
      console.error("Error submitting answer:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const isProblemLocked = (index: number) => {
    const releasedProblems = room?.kachidokiStatus?.releasedProblems || [];
    return !releasedProblems.includes(index);
  };

  const hasAnsweredProblem = (problemId: string) => {
    return mySubmissions.some(sub => sub.problemId === problemId && sub.isCorrect);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const goToPreviousProblem = () => {
    const releasedProblems = room?.kachidokiStatus?.releasedProblems || [];
    const previousIndex = currentProblemIndex - 1;
    if (previousIndex >= 0 && releasedProblems.includes(previousIndex)) {
      setCurrentProblemIndex(previousIndex);
      setAnswer("");
      setLastResult(null);
    }
  };

  const goToNextProblem = () => {
    if (!event) return;
    const releasedProblems = room?.kachidokiStatus?.releasedProblems || [];
    const nextIndex = currentProblemIndex + 1;
    if (nextIndex < event.problems.length && releasedProblems.includes(nextIndex)) {
      setCurrentProblemIndex(nextIndex);
      setAnswer("");
      setLastResult(null);
    }
  };

  const goToLatestProblem = () => {
    const releasedProblems = room?.kachidokiStatus?.releasedProblems || [];
    if (releasedProblems.length > 0) {
      const latestIndex = Math.max(...releasedProblems);
      if (latestIndex !== currentProblemIndex) {
        setCurrentProblemIndex(latestIndex);
        setAnswer("");
        setLastResult(null);
      }
    }
  };

  const canGoPrevious = () => {
    const releasedProblems = room?.kachidokiStatus?.releasedProblems || [];
    return currentProblemIndex > 0 && releasedProblems.includes(currentProblemIndex - 1);
  };

  const canGoNext = () => {
    if (!event) return false;
    const releasedProblems = room?.kachidokiStatus?.releasedProblems || [];
    return currentProblemIndex < event.problems.length - 1 && releasedProblems.includes(currentProblemIndex + 1);
  };

  const isOnLatestProblem = () => {
    const releasedProblems = room?.kachidokiStatus?.releasedProblems || [];
    if (releasedProblems.length === 0) return true;
    const latestIndex = Math.max(...releasedProblems);
    return currentProblemIndex === latestIndex;
  };

  if (!room || !event) {
    return <Container>Loading...</Container>;
  }

  const currentProblem = event.problems[currentProblemIndex];
  const isLocked = isProblemLocked(currentProblemIndex);
  const hasAnswered = currentProblem ? hasAnsweredProblem(currentProblem.problemId) : false;

  return (
    <Container>
      <GlobalStyle />
      <Header>
        <Title>{event.eventName}</Title>
        <StatusInfo>
          {room.kachidokiStatus?.isRunning && nextReleaseIn > 0 && (
            <TimerContainer>
              <TimerBar progress={(nextReleaseIn / (event.kachidokiSettings?.releaseInterval || 30)) * 100} />
              <TimerText>次: {formatTime(nextReleaseIn)}</TimerText>
            </TimerContainer>
          )}
          <Score>スコア: {totalScore}点</Score>
        </StatusInfo>
      </Header>

      <Content>
        <GameCard>
        {isLocked ? (
          <LockedMessage>
            この問題はまだ公開されていません
          </LockedMessage>
        ) : currentProblem && (
          <ProblemContent>
            <ProblemInfo>
              <ProblemNumber>問題 {currentProblemIndex + 1}</ProblemNumber>
              <ProblemStatus status={
                hasAnswered ? 'correct' : 
                mySubmissions.some(sub => sub.problemId === currentProblem.problemId && !sub.isCorrect) ? 'incorrect' : 
                'unanswered'
              }>
                {hasAnswered ? '正解済み' : 
                 mySubmissions.some(sub => sub.problemId === currentProblem.problemId && !sub.isCorrect) ? '不正解' : 
                 '未回答'}
              </ProblemStatus>
            </ProblemInfo>

            {currentProblem.imageUrls && currentProblem.imageUrls[0] && (
              <ProblemImage src={currentProblem.imageUrls[0]} alt="Problem" />
            )}

            <NavigationButtons>
              <NavButton 
                onClick={goToPreviousProblem}
                disabled={!canGoPrevious()}
              >
                ←前
              </NavButton>
              
              <NavButton 
                onClick={goToNextProblem}
                disabled={!canGoNext()}
              >
                次→
              </NavButton>
              
              <NavigationDivider>|</NavigationDivider>
              
              <NavButton 
                onClick={goToLatestProblem}
                disabled={isOnLatestProblem()}
                variant="primary"
              >
                最新
              </NavButton>
            </NavigationButtons>

            <ProblemNavigation>
              <ProblemRow>
                {event.problems.slice(0, 5).map((problem, index) => (
                  <ProblemTab
                    key={problem.problemId}
                    isActive={index === currentProblemIndex}
                    isLocked={isProblemLocked(index)}
                    isAnswered={hasAnsweredProblem(problem.problemId)}
                    onClick={() => !isProblemLocked(index) && setCurrentProblemIndex(index)}
                    disabled={isProblemLocked(index)}
                  >
                    {index + 1}
                  </ProblemTab>
                ))}
              </ProblemRow>
              {event.problems.length > 5 && (
                <ProblemRow>
                  {event.problems.slice(5, 10).map((problem, index) => {
                    const actualIndex = index + 5;
                    return (
                      <ProblemTab
                        key={problem.problemId}
                        isActive={actualIndex === currentProblemIndex}
                        isLocked={isProblemLocked(actualIndex)}
                        isAnswered={hasAnsweredProblem(problem.problemId)}
                        onClick={() => !isProblemLocked(actualIndex) && setCurrentProblemIndex(actualIndex)}
                        disabled={isProblemLocked(actualIndex)}
                      >
                        {actualIndex + 1}
                      </ProblemTab>
                    );
                  })}
                </ProblemRow>
              )}
            </ProblemNavigation>

            {!hasAnswered ? (
              <AnswerForm onSubmit={handleSubmit}>
                <AnswerInput
                  type="text"
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder="答えを入力してください"
                  disabled={submitting}
                  autoComplete="off"
                />
                <SubmitButton type="submit" disabled={submitting || !answer.trim()}>
                  {submitting ? "送信中..." : "回答する"}
                </SubmitButton>
              </AnswerForm>
            ) : (
              <StatusMessage type="success">
                正解済み！
                {mySubmissions.find(sub => sub.problemId === currentProblem.problemId && sub.isCorrect)?.kachidokiRank && (
                  <div style={{ marginTop: '10px' }}>
                    順位: {mySubmissions.find(sub => sub.problemId === currentProblem.problemId && sub.isCorrect)?.kachidokiRank}位
                  </div>
                )}
              </StatusMessage>
            )}

            {lastResult && currentProblem.problemId === event.problems[currentProblemIndex].problemId && (
              <StatusMessage type={lastResult.correct ? 'success' : 'error'}>
                {lastResult.correct ? (
                  <>
                    正解！ +{lastResult.points}点
                    {lastResult.rank && ` (${lastResult.rank}位)`}
                  </>
                ) : (
                  '不正解...'
                )}
              </StatusMessage>
            )}
          </ProblemContent>
        )}
      </GameCard>
      </Content>
    </Container>
  );
};

export default V3KachidokiGame;